import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Phone, Mail, MapPin, X, MessageCircle, LogIn } from 'lucide-react';
import Certifications from './Certifications';
import axios from 'axios';
import { API_URL } from '@/lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/1if4pbt1_steward.png";
const CAREO_LOGO = "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/di9kfck2_53561592_transparent11%20-%20Copy.png";

// High-quality healthcare images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200&h=800&fit=crop",
  doctors: "https://images.pexels.com/photos/5452193/pexels-photo-5452193.jpeg?auto=compress&cs=tinysrgb&w=1200",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&h=800&fit=crop",
  consulting: "https://images.pexels.com/photos/5452285/pexels-photo-5452285.jpeg?auto=compress&cs=tinysrgb&w=1200",
  training: "https://images.pexels.com/photos/36380795/pexels-photo-36380795.jpeg?auto=compress&cs=tinysrgb&w=1200",
  quality: "https://images.pexels.com/photos/6129105/pexels-photo-6129105.jpeg?auto=compress&cs=tinysrgb&w=1200",
  digital: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=800&fit=crop",
  medical: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1200&h=800&fit=crop"
};

const allNavLinks = [
  { name: 'Home', path: '/', key: null },
  { name: 'About', path: '/about', key: 'page_about_visible' },
  { name: 'Services', path: '/services', key: 'page_services_visible' },
  { name: 'Digital Products/EMR', path: '/products', key: 'page_products_visible' },
  { name: 'Courses', path: '/courses', key: 'page_courses_visible' },
  { name: 'For Hospital/Institute', path: 'https://stewardlms.com/for-hospitals', external: true, key: 'page_hospital_visible' },
  { name: 'Our Training Program', path: 'https://stewardlms.com/courses', external: true, key: 'page_training_visible' },
  { name: 'Gallery', path: '/gallery', key: 'page_gallery_visible' },
  { name: 'Blog', path: '/blog', key: 'page_blog_visible' },
  { name: 'Contact', path: '/contact', key: 'page_contact_visible' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navLinks, setNavLinks] = useState(allNavLinks);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchVisibility = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const s = res.data;
        setNavLinks(allNavLinks.filter(link => !link.key || s[link.key] !== false));
      } catch (error) {
        setNavLinks(allNavLinks);
      }
    };
    fetchVisibility();
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'}`}>
      {/* Top bar */}
      <div className="bg-[#1a2a5e] text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+919616298162" className="flex items-center gap-2 hover:text-[#2eb24c] transition-colors">
              <Phone size={14} />
              <span>+91 9616298162</span>
            </a>
            <a href="mailto:info@stewardindia.com" className="flex items-center gap-2 hover:text-[#2eb24c] transition-colors">
              <Mail size={14} />
              <span>info@stewardindia.com</span>
            </a>
          </div>
          <a href="https://app.stewardindia.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#2eb24c] transition-colors font-medium">
            <LogIn size={14} />
            <span>Login</span>
          </a>
        </div>
      </div>

      {/* Main navbar */}
      <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={LOGO_URL} alt="Steward Healthcare" className="h-12 md:h-14 object-contain" />
        </Link>

        {/* Desktop navigation */}
        <div className="hidden lg:flex items-center gap-0">
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-2 rounded-lg font-medium text-xs transition-all text-gray-600 hover:text-[#1a2a5e] hover:bg-gray-100 whitespace-nowrap"
              >
                {link.name}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`px-2.5 py-2 rounded-lg font-medium text-xs transition-all whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-[#1a2a5e] bg-[#1a2a5e]/10'
                    : 'text-gray-600 hover:text-[#1a2a5e] hover:bg-gray-100'
                }`}
              >
                {link.name}
              </Link>
            )
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Button asChild variant="outline" className="border-[#1a2a5e] text-[#1a2a5e] hover:bg-[#1a2a5e] hover:text-white">
            <a href="tel:+919616298162">
              <Phone size={16} className="mr-2" />
              Call Now
            </a>
          </Button>
          <Button asChild className="bg-[#2eb24c] hover:bg-[#259a40] text-white">
            <Link to="/contact">Get Consultation</Link>
          </Button>
        </div>

        {/* Mobile menu button */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon">
              <Menu size={24} />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] p-0">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <img src={LOGO_URL} alt="Steward Healthcare" className="h-10 object-contain" />
              </div>
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 rounded-lg font-medium transition-all text-gray-600 hover:text-[#1a2a5e] hover:bg-gray-100"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        location.pathname === link.path
                          ? 'text-[#1a2a5e] bg-[#1a2a5e]/10'
                          : 'text-gray-600 hover:text-[#1a2a5e] hover:bg-gray-100'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
              </nav>
              <div className="mt-8 flex flex-col gap-3">
                <Button asChild className="w-full bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                  <a href="https://app.stewardindia.com/" target="_blank" rel="noopener noreferrer">
                    <LogIn size={16} className="mr-2" />
                    Login
                  </a>
                </Button>
                <Button asChild className="w-full bg-white text-[#1a2a5e] border border-[#1a2a5e] hover:bg-[#1a2a5e] hover:text-white">
                  <a href="tel:+919616298162">
                    <Phone size={16} className="mr-2" />
                    +91 9616298162
                  </a>
                </Button>
                <Button asChild className="w-full bg-[#2eb24c] hover:bg-[#259a40]">
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    Get Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#1a2a5e] text-white">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img src={LOGO_URL} alt="Steward Healthcare" className="h-12 mb-4 brightness-0 invert" />
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Your trusted partner in healthcare excellence. We provide comprehensive healthcare consulting, accreditation services, and digital solutions.
            </p>
            <a href="http://stewardlms.com/" target="_blank" rel="noopener noreferrer" title="SIHMT – Steward Institute of Healthcare Management & Technology">
              <img 
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_87c0864c-f08f-4236-ae3c-b73a794502ae/artifacts/0zpklhlj_SIHMT%20BG%20Remove.png" 
                alt="SIHMT logo – Steward Institute of Healthcare Management & Technology" 
                className="h-12 mb-4 brightness-0 invert" 
              />
            </a>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              SIHMT – Steward Institute of Healthcare Management & Technology is the healthcare education and training division of Steward Healthcare India Pvt. Ltd. SIHMT offers online and practical training programs in hospital infection prevention and control, healthcare quality, NABH accreditation, nursing and hospital management.
            </p>
            <div className="flex gap-3">
              <a href="https://wa.me/919616298162" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#25D366] transition-colors">
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {allNavLinks.map((link) => (
                <li key={link.path}>
                  {link.external ? (
                    <a href={link.path} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#2eb24c] transition-colors text-sm">
                      {link.name}
                    </a>
                  ) : (
                    <Link to={link.path} className="text-gray-300 hover:text-[#2eb24c] transition-colors text-sm">
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-4">Our Services</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/services/nabh-entry-level" className="hover:text-[#2eb24c] transition-colors">NABH Accreditation</Link></li>
              <li><Link to="/services/nabl" className="hover:text-[#2eb24c] transition-colors">NABL Certification</Link></li>
              <li><Link to="/services/digital-healthcare" className="hover:text-[#2eb24c] transition-colors">Digital Healthcare</Link></li>
              <li><Link to="/services/hospital-planning" className="hover:text-[#2eb24c] transition-colors">Hospital Planning</Link></li>
              <li><Link to="/courses" className="hover:text-[#2eb24c] transition-colors">Healthcare Courses</Link></li>
              <li><a href="https://www.sihmt.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#2eb24c] transition-colors">SIHMT Portal</a></li>
              <li><a href="https://stewardlms.com/verify-certificate" target="_blank" rel="noopener noreferrer" className="hover:text-[#2eb24c] transition-colors">Verify Certificate</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#2eb24c] mt-0.5 flex-shrink-0" />
                <span>SCO 23, 2nd Floor, Hi Street Market, Eldeco Estate One, Panipat-132103</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#2eb24c] flex-shrink-0" />
                <a href="tel:+919616298162" className="hover:text-[#2eb24c] transition-colors">+91 9616298162</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#2eb24c] flex-shrink-0" />
                <a href="mailto:info@stewardindia.com" className="hover:text-[#2eb24c] transition-colors">info@stewardindia.com</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Steward Healthcare India Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-4 mt-2 md:mt-0">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/admin/login" className="hover:text-white transition-colors">Admin Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/919616298162?text=Hello, I'm interested in your healthcare consulting services."
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-button"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-[100px] md:pt-[140px]">
        {children}
      </main>
      <Certifications />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
